<div
     class="campaign-items-container"
     snAssistantSurface="campaignItem"
     i18n-surfaceTitle="@@campaignItem.surface"
     surfaceTitle="Campaign contacts">
  <!-- Header -->
  <div class="campaign-items-header">
    <div class="title-section">
      <div class="title-row">
        <p-button
          icon="pi pi-arrow-left"
          [text]="true"
          size="small"
          (click)="goBackToCampaign()">
        </p-button>
        <h1>{{ campaign?.title || campaignItemsFallback }}</h1>
      </div>
      <p class="subtitle" i18n="@@campaignItems.subtitle">Manage contacts and their variables for this campaign</p>
    </div>
    <div class="actions-section">
      <p-splitbutton
        [label]="isDownloadingTemplate ? downloadingLabel : addItemLabel"
        [icon]="isDownloadingTemplate ? 'pi pi-spin pi-spinner' : 'pi pi-plus'"
        (onClick)="addNewItem()"
        [model]="addItemMenuItems"
        [disabled]="!canAddOrEditItems() || isDownloadingTemplate"
        snAssistantAction="add"
        actionHint="Opens an empty contact form. Nobody is dialled until the campaign itself is started.">
      </p-splitbutton>
    </div>
  </div>

  <!-- Hidden file input for Excel upload -->
  <input
    #fileInput
    type="file"
    accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    (change)="onFileSelected($event)"
    style="display: none">

  <!-- Upload Progress -->
  @if (isUploading) {
    <div class="upload-progress-bar">
      <p-progressBar [value]="uploadProgress"></p-progressBar>
      <p class="progress-text"><ng-container i18n="@@campaignItems.uploading">Uploading file...</ng-container> {{ uploadProgress | number:'1.0-0' }}%</p>
    </div>
  }

  <!-- Assigning File -->
  @if (isAssigningFile) {
    <div class="upload-progress-bar">
      <p-progressBar mode="indeterminate" [style]="{ height: '6px' }"></p-progressBar>
      <p class="progress-text" i18n="@@campaignItems.linking">Linking file to campaign...</p>
    </div>
  }

  <!-- File Preview & Column Mapping -->
  @if (!isUploading && !isAssigningFile && filePreviewSheet) {
    <p-accordion [(value)]="filePreviewActivePanel" [multiple]="true" class="file-preview-accordion">
      <p-accordion-panel value="file-preview">
        <p-accordion-header>
          <div class="file-preview-title">
            <i class="pi pi-file-excel"></i>
            <span i18n="@@campaignItems.filePreview">File Preview</span>
            <span class="file-preview-info">{{ filePreviewSheet.row_count }} <ng-container i18n="@@campaignItems.rows">rows</ng-container>, {{ filePreviewSheet.column_count }} <ng-container i18n="@@campaignItems.columns">columns</ng-container></span>
          </div>
        </p-accordion-header>
        <p-accordion-content>
          <div class="file-preview-toolbar">
            <div class="first-row-toggle">
              <p-toggleSwitch
                [(ngModel)]="firstRowIsHeader"
                [snAssistantId]="F.firstRowIsHeader"
                snAssistantLabel="First row is headers"
                [disabled]="!isInitialStatus"
                (onChange)="onFirstRowIsHeaderChange()">
              </p-toggleSwitch>
              <span class="toggle-label" i18n="@@campaignItems.firstRowHeader">First row is headers</span>
            </div>
            <p-button
              i18n-label="@@campaignItems.saveMapping"
              label="Save Mapping"
              icon="pi pi-check"
              size="small"
              (click)="saveColumnMapping()"
              [disabled]="!isInitialStatus || !hasMappingChanges || isSavingMapping"
              [loading]="isSavingMapping"
              snAssistantAction="saveMapping"
              actionHint="Writes how the uploaded file maps onto contact fields, and builds the contact list from it.">
            </p-button>
          </div>
          <div class="mapping-description">
            <p i18n="@@campaignItems.mapping.desc1">Map Excel columns to system fields. <strong>Phone Number</strong> is required.</p>
            <p i18n="@@campaignItems.mapping.desc2">Fields in <code>{{ curlyBraces }}</code> are agent variables — if not mapped, default values from the agent settings will be used.</p>
          </div>
          <div class="file-preview-table-wrapper">
            <table class="file-preview-table">
              <thead>
                <tr class="mapping-row">
                  @for (col of columnDefs; track col.columnIndex) {
                    <th>
                      <p-select
                        [(ngModel)]="columnMapping[col.columnIndex]"
                        [snAssistantId]="'column' + col.columnIndex"
                        [snAssistantLabel]="'Column ' + col.header + ' maps to'"
                        [options]="getAvailableOptionsForColumn(col.columnIndex)"
                        optionLabel="label"
                        optionValue="value"
                        i18n-placeholder="@@campaignItems.notMapped"
                        placeholder="— Not mapped —"
                        [showClear]="true"
                        [disabled]="!isInitialStatus"
                        (onChange)="onColumnMappingChange()"
                        appendTo="body"
                        class="mapping-select">
                      </p-select>
                    </th>
                  }
                </tr>
                <tr>
                  @for (col of columnDefs; track col.columnIndex) {
                    <th>{{ col.header }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of dataRows; track row.row_number) {
                  <tr>
                    @for (col of columnDefs; track col.columnIndex) {
                      <td>{{ getCellValue(row, col.columnIndex) }}</td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </p-accordion-content>
      </p-accordion-panel>
    </p-accordion>
  }

  <!-- Filters Panel -->
  <div class="filters-panel">
    <div class="filters-grid">
      <div class="filter-item">
        <label for="status-filter" i18n="@@conversations.filter.status">Status</label>
        <p-select
          id="status-filter"
          [(ngModel)]="selectedStatus"
          snAssistantSkip
          [options]="statusOptions"
          optionLabel="label"
          optionValue="value"
          i18n-placeholder="@@conversations.filter.allStatuses"
          placeholder="All statuses"
          [showClear]="true"
          (onChange)="onFilterChange()"
          class="filter-select">
        </p-select>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  @if (isLoadingCampaign) {
    <div class="loading-container">
      <p-progressSpinner [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
      <p i18n="@@campaignItems.loadingCampaign">Loading campaign and agent information...</p>
    </div>
  }

  <!-- Table -->
  @if (!isLoadingCampaign) {
    <div class="table-wrapper">
      <p-table
        #dt
        [value]="items"
        [virtualScroll]="true"
        [virtualScrollItemSize]="60"
        [virtualScrollOptions]="virtualScrollOptions"
        [rowTrackBy]="trackByItemId"
        class="items-table"
        [scrollable]="true"
        scrollHeight="flex">
        <ng-template #header>
          <tr>
            <th style="width: 18%;" i18n="@@campaignEdit.phoneNumber.label">Phone Number</th>
            <th style="width: 10%;" i18n="@@agentEdit.voice.language.label">Language</th>
            <th class="desktop-only" style="width: 22%;" i18n="@@agentEdit.firstMessage.label">First Message</th>
            <th style="width: 10%;" i18n="@@conversations.col.status">Status</th>
            <th class="desktop-only" style="width: 25%;" i18n="@@campaignItems.col.error">Error</th>
            <th style="width: 15%;" i18n="@@campaignItems.col.actions">Actions</th>
          </tr>
        </ng-template>

        <ng-template #body let-rowData>
          <tr class="item-row">
            <td>
              @if (rowData.session_ids?.length && campaign?.agent_id) {
                <a [routerLink]="['/agents', campaign!.agent_id, 'conversations', rowData.session_ids[0]]"
                   class="session-link"
                   [pTooltip]="goToConversationLabel"
                   tooltipPosition="top">
                  {{ rowData.phone_number || 'N/A' }} <i class="pi pi-external-link"></i>
                </a>
              } @else {
                {{ rowData.phone_number || 'N/A' }}
              }
            </td>
            <td>{{ getLocaleDisplay(rowData.language_locale_id) }}</td>
            <td class="desktop-only">
              <span class="first-message-cell">{{ rowData.first_message || '-' }}</span>
            </td>
            <td>
              <p-tag
                [value]="getItemStatusLabel(rowData.status, rowData.failed)"
                [severity]="rowData.failed ? 'danger' : getStatusSeverity(rowData.status)">
              </p-tag>
            </td>
            <td class="desktop-only">
              <span class="error-message">{{ rowData.error_message || '-' }}</span>
            </td>
            <td>
              <div class="actions-cell">
                <p-button
                  icon="pi pi-pencil"
                  severity="secondary"
                  [text]="true"
                  size="small"
                  (click)="editItem(rowData, $event)"
                  [disabled]="!canAddOrEditItems()"
                  [snAssistantAction]="'edit.' + rowData.id"
                  [actionLabel]="'Edit contact ' + rowData.phone_number"
                  actionHint="Opens this contact in the side form — its fields become available after that.">
                </p-button>
                <p-button
                  icon="pi pi-trash"
                  severity="danger"
                  [text]="true"
                  size="small"
                  (click)="deleteItem(rowData, $event)"
                  [snAssistantAction]="'delete.' + rowData.id"
                  [actionLabel]="'Delete contact ' + rowData.phone_number"
                  actionDangerous
                  actionHint="Removes the contact from the campaign, so it will not be called.">
                </p-button>
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          @if (!isLoading) {
            <tr>
              <td colspan="6" class="empty-message" i18n="@@campaignItems.empty">
                No items found. Add your first item to get started.
              </td>
            </tr>
          }
        </ng-template>

        <ng-template pTemplate="footer">
          @if (isLoading) {
            <tr>
              <td colspan="6" style="text-align: center; padding: 1rem;">
                <p-progressSpinner [style]="{ width: '30px', height: '30px' }" />
              </td>
            </tr>
          }
        </ng-template>
      </p-table>
    </div>
  }

  <!-- Mobile Bottom Bar -->
  <div class="mobile-bottom-bar">
    <div class="mobile-bottom-actions">
      <p-splitbutton
        [label]="isDownloadingTemplate ? downloadingLabel : addItemLabel"
        [icon]="isDownloadingTemplate ? 'pi pi-spin pi-spinner' : 'pi pi-plus'"
        (onClick)="addNewItem()"
        [model]="addItemMenuItems"
        [disabled]="!canAddOrEditItems() || isDownloadingTemplate">
      </p-splitbutton>
    </div>
  </div>
</div>

<!-- Item Edit Drawer -->
<p-drawer
  [(visible)]="showItemDrawer"
  position="right"
  [modal]="true"
  [dismissible]="true"
  styleClass="item-drawer"
  (onHide)="closeItemDrawer()">
  <ng-template #header>
    <h3>{{ editingItemIndex === -1 ? addItemLabel : editItemTitle }}</h3>
  </ng-template>

  <div
    class="drawer-content"
    snAssistantSurface="campaignItem"
    i18n-surfaceTitle="@@campaignItem.surface"
    surfaceTitle="Campaign contacts">
    <!-- Phone Number Field -->
    <div class="form-field">
      <label for="phone-number" class="field-label"><ng-container i18n="@@campaignEdit.phoneNumber.label">Phone Number</ng-container> *</label>
      <p class="field-description" i18n="@@campaignItems.phone.description">Contact phone number for this campaign item</p>
      <input
        pInputText
        id="phone-number"
        [(ngModel)]="editingItem.phone_number"
        [snAssistantId]="F.phoneNumber"
        placeholder="+1234567890"
        class="w-full">
    </div>

    <!-- Send Digits -->
    <div class="form-field">
      <label for="send-digits" class="field-label" i18n="@@campaignItems.sendDigits.label">Send Digits</label>
      <p class="field-description" i18n="@@campaignItems.sendDigits.description">DTMF digits to send after connection (e.g. extension number)</p>
      <input
        pInputText
        id="send-digits"
        [(ngModel)]="sendDigits"
        [snAssistantId]="F.sendDigits"
        i18n-placeholder="@@campaignItems.sendDigits.placeholder"
        placeholder="e.g. 1234#"
        class="w-full">
    </div>

    <!-- Language Locale Selection -->
    @if (localeOptions.length > 0) {
      <div class="form-field">
        <label for="language-locale" class="field-label" i18n="@@agentEdit.voice.language.label">Language</label>
        <p class="field-description" i18n="@@campaignItems.language.description">Select language for this contact (from agent's languages)</p>
        <p-select
          id="language-locale"
          [(ngModel)]="editingItem.language_locale_id"
          [snAssistantId]="F.languageLocale"
          [options]="localeOptions"
          optionLabel="label"
          optionValue="value"
          i18n-placeholder="@@campaignItems.language.placeholder"
          placeholder="Default (agent setting)"
          [showClear]="true"
          class="w-full">
        </p-select>
      </div>
    }

    <!-- First Message -->
    <div class="form-field">
      <label class="field-label" i18n="@@campaignItems.firstMessage.label">Custom First Message</label>
      <div class="toggle-row">
        <p-toggleSwitch
          [(ngModel)]="useCustomFirstMessage"
          [snAssistantId]="F.useCustomFirstMessage"
          snAssistantLabel="Override the first message"></p-toggleSwitch>
        <span class="toggle-label" i18n="@@campaignItems.firstMessage.toggle">Override agent's first message</span>
      </div>
      @if (useCustomFirstMessage) {
        <textarea
          pTextarea
          [(ngModel)]="editingFirstMessage"
          [snAssistantId]="F.firstMessage"
          i18n-placeholder="@@campaignItems.firstMessage.placeholder"
          placeholder="Enter custom first message..."
          [autoResize]="true"
          rows="3"
          class="w-full">
        </textarea>
      }
    </div>

    <!-- Variables Editor -->
    @if (agentVariablesSchema && agentVariablesSchema.properties) {
      <div class="form-field">
        <label class="field-label" i18n="@@campaignItems.variables.label">Variables</label>
        <p class="field-description" i18n="@@campaignItems.variables.description">Configure variables for this contact based on agent schema</p>

        <app-properties-edit
          [snAssistantAnchor]="F.variables"
          [schemas]="getVariableSchemas()"
          [values]="variableValues"
          (valuesChange)="onVariableValuesChange($event)">
        </app-properties-edit>
      </div>
    }
  </div>

  <ng-template #footer>
    <div class="drawer-footer">
      <p-button
        i18n-label="@@common.cancel"
        label="Cancel"
        severity="secondary"
        [outlined]="true"
        (click)="closeItemDrawer()">
      </p-button>
      <p-button
        i18n-label="@@campaignItems.testCall"
        label="Test Call"
        icon="pi pi-phone"
        severity="info"
        [outlined]="true"
        (click)="testCall()"
        [disabled]="!editingItem.phone_number || isTestingCall"
        [loading]="isTestingCall"
        snAssistantAction="testCall"
        actionDangerous
        actionHint="Places a real call to this number right now, so you can hear what the contact would hear.">
      </p-button>
      @if (testCallSid) {
        <app-live-listen-button
          [agentId]="campaign?.agent_id || null"
          [sessionId]="testCallSessionId" />
      }
      <p-button
        i18n-label="@@common.save"
        label="Save"
        icon="pi pi-check"
        (click)="saveItemEdit()"
        [disabled]="!editingItem.phone_number"
        snAssistantAction="save"
        actionHint="Writes the contact into the campaign list.">
      </p-button>
    </div>
  </ng-template>
</p-drawer>

<!-- Delete Confirmation Dialog -->
<app-delete-confirmation
  [(visible)]="showDeleteDialog"
  [title]="deleteTitle"
  [subtitle]="deleteSubtitle"
  [deleting]="isDeleting"
  (confirm)="onDeleteConfirm()">
</app-delete-confirmation>
